import methods from '../client/httpMethods'
import request from '../client/request'

/**
 * Service for handling after-sale operations.
 *
 * @param {Object} dependencies - The dependencies required for the service.
 * @param {Function} dependencies.post - HTTP POST method.
 * @param {Function} dependencies.patch - HTTP PATCH method.
 * @returns {Object} - The after-sale service methods.
 */
const afterSaleService = ({ post, patch }) => ({
  /**
   * Creates a new after-sale record.
   *
   * @param {Object} params - The parameters for creating the after-sale record.
   * @param {string} params.id - The ID of the suborder.
   * @param {string} params.type - The type of the after-sale operation.
   * @param {Array} params.items - The list of items involved in the operation.
   * @param {string} params.status - The status of the after-sale operation.
   * @param {number} params.penalty - The penalty amount, if applicable.
   * @param {Object} params.transport - The transport details for the operation.
   * @param {string} params.reason - The reason for the after-sale operation.
   * @param {boolean} params.isWithdrawable - Indicates if the operation is withdrawable.
   * @param {Object} [params.cancelToken] - The cancel token for the request.
   * @returns {Promise<Object|Error>} - The response data or an error.
   */

  create: async ({
    id,
    type,
    items,
    status,
    penalty,
    transport,
    reason,
    isWithdrawable,
    cancelToken
  }) => {
    try {
      const response = await request(`/suborder/aftersale`, {
        method: post,
        data: JSON.stringify({
          id,
          type,
          status,
          reason,
          items,
          transport,
          isWithdrawable,
          penalty
        }),
        cancelToken
      })
      const currentData = await response.data
      return currentData
    } catch (error) {
      return error
    }
  },

  /**
   * Updates an existing after-sale record.
   *
   * @param {Object} params - The parameters for updating the after-sale record.
   * @param {string} params.id - The ID of the suborder.
   * @param {string} params.type - The type of the after-sale operation.
   * @param {Array} params.items - The list of items involved in the operation.
   * @param {string} params.status - The status of the after-sale operation.
   * @param {number} params.penalty - The penalty amount, if applicable.
   * @param {Object} params.transport - The transport details for the operation.
   * @param {string} params.reason - The reason for the after-sale operation.
   * @param {boolean} params.isWithdrawable - Indicates if the operation is withdrawable.
   * @param {Object} [params.cancelToken] - The cancel token for the request.
   * @returns {Promise<Object|Error>} - The response data or an error.
   */

  update: async ({ id, type, status, cancelToken }) => {
    try {
      const response = await request(`/suborder/aftersale`, {
        method: patch,
        data: JSON.stringify({
          id,
          type,
          status
        }),
        cancelToken
      })
      return await response.data
    } catch (error) {
      return error
    }
  }
})

export default afterSaleService(methods)
